import React, {useCallback, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {useFormik} from 'formik'
import moment from 'moment'
import {KTCard, KTCardBody} from '../../../_websoft/helpers'
import {getIzvjestajPoTipuThermoboard} from '../../api/thermoboardNalozi'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../components/Toast/Toast'
import PanelTipDropdown from '../paneli/components/PanelTipDropdown/PanelTipDropdown'

interface Props {
    tipPanela?: number | string
}

type PackageRow = {
    Id: number | string
    IdNaloga?: number | string
    BrojFmkNaloga?: string | null
    KupacNaziv?: string | null
    Komercijalista?: string | null
    Sirina: number | string
    Duzina: number | string
    Komada: number | string
    Status?: number | string
    Proizvedeno?: number | string
    Isporuceno?: number | string
    DatumIsporuke?: string | null
    ProizvedenDatum?: string | null
    PaketOtpremljen?: number | string
}

type ReportRow = {
    Id: number | string
    TipPanela?: number | string
    Napomena?: string | null
    Status?: number | string
    Kreirano?: string
    proizvedeno_paketa?: number | string
    isporuceno_paketa?: number | string
    za_proizvest_paketa?: number | string
    tip_panela?: {
        Id?: number | string
        Naziv?: string
        naziv?: string
    } | null
    paketi?: PackageRow[]
    paketi_za_proizvest?: PackageRow[]
}

type Totals = {
    ukupnoM2?: number | string
    ukupnoPaketa?: number | string
    ukupnoNaloga?: number | string
}

const AREA_DIVISOR = 1000000

const toNumber = (value?: number | string | null) => {
    const parsedValue = Number(value)

    return Number.isFinite(parsedValue) ? parsedValue : 0
}

const calculatePackageM2 = (paket: PackageRow) =>
    (toNumber(paket.Sirina) * toNumber(paket.Duzina) * toNumber(paket.Komada)) / AREA_DIVISOR

const formatNumber = (value: number | string) =>
    Number(value).toLocaleString('bs-BA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

const formatDateTime = (value?: string | null) =>
    value && moment(value).isValid() ? moment(value).format('DD-MM-YYYY HH:mm') : '-'

const getTipPanelaLabel = (row: ReportRow) =>
    row.tip_panela?.Naziv || row.tip_panela?.naziv || row.TipPanela || '-'

const getPackageStatus = (paket: PackageRow) => {
    if (String(paket.Isporuceno) === '1') {
        return {label: 'Isporucen', badgeClass: 'badge-light-primary'}
    }

    if (String(paket.Proizvedeno) === '0') {
        return {label: 'Nije proizveden', badgeClass: 'badge-light-danger'}
    }

    switch (String(paket.Status)) {
        case '1':
            return {label: 'Raspoloziv', badgeClass: 'badge-light-success'}
        case '2':
            return {label: 'Rezervisan', badgeClass: 'badge-light-danger'}
        default:
            return {label: paket.Status ? String(paket.Status) : '-', badgeClass: 'badge-light'}
    }
}

const getNalogTotalM2 = (paketi: PackageRow[]) =>
    paketi.reduce((total, paket) => total + calculatePackageM2(paket), 0)

const getNalogKomada = (paketi: PackageRow[]) =>
    paketi.reduce((total, paket) => total + toNumber(paket.Komada), 0)

const getReportTotals = (rows: ReportRow[], apiTotals: Totals | null) => {
    const paketi = rows.flatMap((row) => row.paketi ?? [])

    return {
        ukupnoM2:
            apiTotals?.ukupnoM2 ?? paketi.reduce((total, paket) => total + calculatePackageM2(paket), 0),
        ukupnoPaketa: apiTotals?.ukupnoPaketa ?? paketi.length,
        ukupnoNaloga: apiTotals?.ukupnoNaloga ?? rows.length,
        proizvedenoPaketa: rows.reduce((total, row) => total + toNumber(row.proizvedeno_paketa), 0),
        isporucenoPaketa: rows.reduce((total, row) => total + toNumber(row.isporuceno_paketa), 0),
        zaProizvestPaketa: rows.reduce((total, row) => total + toNumber(row.za_proizvest_paketa), 0),
    }
}

const ThermoboardIzvjestajPoTipu = ({tipPanela}: Props) => {
    const [loading, setLoading] = useState(false)
    const [reportRows, setReportRows] = useState<ReportRow[]>([])
    const [totali, setTotali] = useState<Totals | null>(null)

    const fetchReport = useCallback(
        async (selectedTipPanela?: number | string, selectedIsporucen?: number | string) => {
            if (!selectedTipPanela || selectedIsporucen === undefined || selectedIsporucen === '') {
                setReportRows([])
                setTotali(null)
                return
            }

            setLoading(true)

            try {
                const response = await getIzvjestajPoTipuThermoboard({
                    tipPloce: selectedTipPanela,
                    isporucen: selectedIsporucen,
                })

                setReportRows(response.data?.data ?? [])
                setTotali(response.data?.totali ?? null)
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message || error.message || 'Greska pri ucitavanju.')
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const formik = useFormik({
        initialValues: {
            tipPanela: tipPanela ?? '',
            isporucen: '',
        },
        enableReinitialize: true,
        validate: (values) => {
            const errors: { tipPanela?: string; isporucen?: string } = {}

            if (!values.tipPanela) {
                errors.tipPanela = 'Odaberite tip panela'
            }

            if (values.isporucen === '') {
                errors.isporucen = 'Odaberite status isporuke'
            }

            return errors
        },
        onSubmit: async (values) => {
            await fetchReport(values.tipPanela, values.isporucen)
        },
    })

    const computedTotals = getReportTotals(reportRows, totali)
    const showPaketiZaProizvest = String(formik.values.isporucen) === '0'

    const totalCards = [
        {
            label: 'Ukupno m2',
            value: `${formatNumber(computedTotals.ukupnoM2)} m2`,
            className: 'bg-light-primary text-primary',
        },
        {
            label: 'Raspolozivo paketa',
            value: computedTotals.ukupnoPaketa,
            className: 'bg-light-success text-success',
        },
        {
            label: 'Proizvedeno pak.',
            value: computedTotals.proizvedenoPaketa,
            className: 'bg-light-success text-success',
        },
        {
            label: 'Isporuceno pak.',
            value: computedTotals.isporucenoPaketa,
            className: 'bg-light-primary text-primary',
        },
        {
            label: 'Za proizvest pak.',
            value: computedTotals.zaProizvestPaketa,
            className: 'bg-light-danger text-danger',
        },
    ]

    return (
        <>
            <ToastContainer/>

            <KTCard>
                <KTCardBody className='py-6'>
                    <form onSubmit={formik.handleSubmit} className='form'>
                        <div className='row g-5 align-items-start'>
                            <div className='col-12 col-lg-5'>
                                <label className='form-label required fw-bold fs-6'>Tip panela</label>
                                <PanelTipDropdown
                                    value={formik.values.tipPanela}
                                    onChange={(_, value) => {
                                        formik.setFieldValue('tipPanela', value)
                                        formik.setFieldTouched('tipPanela', true, false)
                                    }}
                                    errors={formik.errors.tipPanela}
                                    touched={formik.touched.tipPanela}
                                    selectName='tipPanela'
                                />
                                <div className='text-muted fs-7 mt-2'>Odaberite tip panela za pregled naloga.</div>
                            </div>

                            <div className='col-12 col-lg-3'>
                                <label className='form-label required fw-bold fs-6'>Isporucen</label>
                                <select
                                    className={`form-select form-select-lg form-select-solid ${
                                        formik.touched.isporucen && formik.errors.isporucen ? 'is-invalid' : ''
                                    }`}
                                    value={formik.values.isporucen}
                                    onChange={(event) => {
                                        formik.setFieldValue('isporucen', event.target.value)
                                        formik.setFieldTouched('isporucen', true, false)
                                    }}
                                >
                                    <option value=''>Odaberite</option>
                                    <option value='0'>Nije isporucen</option>
                                    <option value='1'>Isporucen</option>
                                </select>
                                {formik.touched.isporucen && formik.errors.isporucen && (
                                    <div className='invalid-feedback d-block'>{formik.errors.isporucen}</div>
                                )}
                            </div>

                            <div className='col-12 col-lg-auto pt-lg-8'>
                                <button
                                    type='submit'
                                    className='btn btn-primary btn-lg px-8 w-100'
                                    disabled={loading || formik.isSubmitting}
                                >
                                    {!loading && 'Prikazi'}
                                    {loading && (
                                        <span className='indicator-progress' style={{display: 'block'}}>
                      Ucitavanje...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </KTCardBody>
            </KTCard>

            <div className='mt-6'>
                {reportRows.length > 0 && (
                    <div className='row g-5 mb-6'>
                        {totalCards.map((card) => (
                            <div className='col-xl-2 col-md-4 col-sm-6' key={card.label}>
                                <div className={`card shadow-sm h-100 ${card.className}`}>
                                    <div className='card-body py-5'>
                                        <div className='fs-7 fw-bold text-uppercase opacity-75 mb-2'>{card.label}</div>
                                        <div className='fs-2x fw-bolder'>{card.value}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <KTCard>
                    <KTCardBody className='py-4'>
                        {loading ? (
                            <LoadingComponent/>
                        ) : (
                            <div className='table-responsive'>
                                <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-4'>
                                    <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                        <th className='ps-4 min-w-140px rounded-start'>Nalog / paket</th>
                                        <th className='min-w-150px'>Tip panela</th>
                                        <th className='min-w-220px'>Napomena</th>
                                        <th className='min-w-160px'>Dimenzije</th>
                                        <th className='min-w-100px text-center'>Komada</th>
                                        <th className='min-w-160px text-center'>Status</th>
                                        <th className='min-w-170px'>Datumi</th>
                                        <th className='min-w-130px text-end rounded-end pe-4'>m2</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {reportRows.length > 0 ? (
                                        reportRows.map((nalog) => {
                                            const packages = nalog.paketi ?? []
                                            const packagesToProduce = showPaketiZaProizvest
                                                ? nalog.paketi_za_proizvest ?? []
                                                : []
                                            const nalogTotalM2 = getNalogTotalM2(packages)
                                            const packagesToProduceTotalM2 = getNalogTotalM2(packagesToProduce)

                                            return (
                                                <React.Fragment key={nalog.Id}>
                                                    <tr style={{backgroundColor: '#F1F5F9'}}>
                                                        <td className='ps-4 py-5'>
                                                            <span
                                                                className='text-gray-900 fw-bold d-block'>Nalog #{nalog.Id}</span>
                                                            <span
                                                                className='text-muted fs-7'>Kreirano: {formatDateTime(nalog.Kreirano)}</span>
                                                        </td>
                                                        <td className='py-5'>
                                                            <span className='text-gray-900 fw-bold d-block'>
                                                              {getTipPanelaLabel(nalog)}
                                                            </span>
                                                            <span
                                                                className='text-muted fs-7'>Tip panela</span>
                                                        </td>
                                                        <td className='py-5'>
                                                                <span className='text-gray-800 fw-semibold d-block'>
                                                                  {nalog.Napomena || '-'}
                                                                </span>
                                                            <span className='text-muted fs-7'>Napomena</span>
                                                        </td>
                                                        <td className='py-5'>
                                                            <span className='text-muted'>Nalog zbir prikazan iznad</span>
                                                        </td>
                                                        <td className='text-center py-5'>
                                <span className='badge badge-light-info fs-7 fw-bold px-4 py-3'>
                                  {getNalogKomada(packages)}
                                </span>
                                                        </td>
                                                        <td className='text-center py-5'>
                                <span className='badge badge-light-dark fs-7 fw-bold px-4 py-3'>
                                  {packages.length} paketa
                                </span>
                                                        </td>
                                                        <td className='py-5'>
                                                            <span className='text-muted'>-</span>
                                                        </td>
                                                        <td className='text-end pe-4 py-5'>
                                <span className='badge badge-light-info fs-6 fw-bolder px-4 py-3'>
                                  {formatNumber(nalogTotalM2)} m2
                                </span>
                                                        </td>
                                                    </tr>

                                                    {packages.length > 0 ? (
                                                        packages.map((paket) => {
                                                            const status = getPackageStatus(paket)

                                                            return (
                                                                <tr key={`${nalog.Id}-${paket.Id}`}>
                                                                    <td className='ps-4'>
                                                                        <span className='badge badge-light fw-bold'>Paket #{paket.Id}</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className='text-muted'>{getTipPanelaLabel(nalog)}</span>
                                                                    </td>
                                                                    <td>
                                      <span className='text-gray-800 fw-semibold d-block'>
                                        {paket.KupacNaziv || '-'}
                                      </span>
                                                                        <span
                                                                            className='text-muted fs-7'>{paket.BrojFmkNaloga || '-'}</span>
                                                                    </td>
                                                                    <td>
                                      <span className='text-muted'>
                                        {paket.Duzina} x {paket.Sirina} mm
                                      </span>
                                                                    </td>
                                                                    <td className='text-center'>
                                                                        <span className='fw-bold'>{paket.Komada}</span>
                                                                    </td>
                                                                    <td className='text-center'>
                                      <span className={`badge ${status.badgeClass} fs-7 fw-bold`}>
                                        {status.label}
                                      </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className='d-flex flex-column gap-1'>
                                        <span className='text-muted fs-7'>
                                          Proizveden: {formatDateTime(paket.ProizvedenDatum)}
                                        </span>
                                                                            <span className='text-muted fs-7'>
                                          Isporucen: {formatDateTime(paket.DatumIsporuke)}
                                        </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className='text-end pe-4'>
                                      <span className='fw-bold text-gray-800'>
                                        {formatNumber(calculatePackageM2(paket))} m2
                                      </span>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={8} className='ps-10 text-muted'>
                                                                Nema paketa za ovaj nalog.
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {packagesToProduce.length > 0 && (
                                                        <>
                                                            <tr style={{backgroundColor: '#FFF8DD'}}>
                                                                <td colSpan={8} className='ps-4 py-3'>
                                                                    <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
                                                                        <div>
                                                                            <span className='fw-bold text-danger d-block'>
                                                                                Paketi za proizvest
                                                                            </span>
                                                                            <span className='text-muted fs-7'>
                                                                                Prikazano samo za filter: Nije isporucen
                                                                            </span>
                                                                        </div>
                                                                        <div className='d-flex gap-2'>
                                                                            <span className='badge badge-light-warning fw-bold'>
                                                                                {packagesToProduce.length} paketa
                                                                            </span>
                                                                            <span className='badge badge-light-info fw-bold'>
                                                                                {formatNumber(packagesToProduceTotalM2)} m2
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>

                                                            {packagesToProduce.map((paket) => {
                                                                const status = getPackageStatus(paket)

                                                                return (
                                                                    <tr key={`${nalog.Id}-za-proizvest-${paket.Id}`}>
                                                                        <td className='ps-4'>
                                                                            <span className='badge badge-light-danger fw-bold'>
                                                                                Paket #{paket.Id}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className='text-muted'>
                                                                                {getTipPanelaLabel(nalog)}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className='text-gray-800 fw-semibold d-block'>
                                                                                {paket.KupacNaziv || '-'}
                                                                            </span>
                                                                            <span className='text-muted fs-7'>
                                                                                {paket.BrojFmkNaloga || '-'}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className='text-muted'>
                                                                                {paket.Duzina} x {paket.Sirina} mm
                                                                            </span>
                                                                        </td>
                                                                        <td className='text-center'>
                                                                            <span className='fw-bold'>{paket.Komada}</span>
                                                                        </td>
                                                                        <td className='text-center'>
                                                                            <span className={`badge ${status.badgeClass} fs-7 fw-bold`}>
                                                                                {status.label}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <div className='d-flex flex-column gap-1'>
                                                                                <span className='text-muted fs-7'>
                                                                                    Proizveden: {formatDateTime(paket.ProizvedenDatum)}
                                                                                </span>
                                                                                <span className='text-muted fs-7'>
                                                                                    Isporucen: {formatDateTime(paket.DatumIsporuke)}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td className='text-end pe-4'>
                                                                            <span className='fw-bold text-gray-800'>
                                                                                {formatNumber(calculatePackageM2(paket))} m2
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={8}>
                                                <div className='d-flex justify-content-center py-10 text-muted'>
                                                    Nema podataka za odabrane filtere.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </KTCardBody>
                </KTCard>
            </div>
        </>
    )
}

export default ThermoboardIzvjestajPoTipu
